/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles,
  Typography
} from '@material-ui/core';
import UsersIcon from '@material-ui/icons/Group';
import WorkIcon from '@material-ui/icons/Work';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { PieChart as PieChartIcon } from 'react-feather';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import Logo from 'src/components/Logo';
import { APP_VERSION } from 'src/config';
import { getRoleName } from 'src/utils/role';
import NavItem from './NavItem';

const dashboardItem = {
  title: 'Tablero de control',
  icon: PieChartIcon,
  href: '/dashboard'
};

const adminNavConfig = [
  {
    subheader: 'Menú',
    items: [dashboardItem]
  },
  {
    subheader: 'Administración',
    items: [
      {
        title: 'Pólizas',
        href: '/policies',
        icon: WorkIcon
      },
      {
        title: 'Usuarios',
        href: '/users',
        icon: UsersIcon
      }
    ]
  }
];

const employeeNavConfig = [
  {
    subheader: 'Menú',
    items: [dashboardItem]
  },
  {
    subheader: 'Administración',
    items: [
      {
        title: 'Pólizas',
        href: '/policies',
        icon: WorkIcon
      }
    ]
  }
];

function renderNavItems({ items, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth = 0 }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

function NavBar({ openMobile, onMobileClose, openDesktop }) {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useSelector((state) => state.account);
  const [navigationItems, setNavigationItems] = useState([]);

  useEffect(() => {
    if (user !== null) {
      switch (user.groups[0].name) {
        case 'admin':
          setNavigationItems(adminNavConfig);
          break;
        case 'employee':
          setNavigationItems(employeeNavConfig);
          break;
        default:
      }
    }
  }, [user]);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/dashboard">
              <Avatar alt="User" className={classes.avatar} src={user.photo} />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/dashboard"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {`${user.first_name} ${user.last_name}`}
            </Link>
            <Typography variant="body2" color="textSecondary">
              {getRoleName(user.groups[0].name)}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {navigationItems.map((config) => (
            <List
              key={config.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {config.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        <Box p={2}>
          <Box
            p={2}
            borderRadius="borderRadius"
            bgcolor="background.dark"
            textAlign="center"
          >
            <Typography variant="h6" color="textPrimary">
              {`v${APP_VERSION}`}
            </Typography>
            <Typography variant="h6" color="textPrimary">
              Powered by{' '}
              <Link
                component="a"
                color="secondary"
                href="https://inappsis.com"
                target="_blank"
                underline="always"
              >
                Inappsis
              </Link>
            </Typography>
          </Box>
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open={openDesktop}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  openDesktop: PropTypes.bool
};

export default NavBar;
